import { useState, useEffect, useMemo } from 'react';
import { Flavor, Filters, applyFilters, sortFlavors } from '../utils/filterUtils';
import flavorData from '../data/flavors.json';
import { getAllRatings } from '../utils/githubUtils';

/**
 * Custom hook for loading and filtering flavors
 * @param initialFilters - Initial filter criteria
 * @param initialSortBy - Initial sort criterion
 * @returns - Object containing flavors, filtered flavors, and filter/sort functions
 */
export const useFlavors = (initialFilters: Filters = {}, initialSortBy: string = 'popularity') => {
  // State for all flavors
  const [flavors, setFlavors] = useState<Flavor[]>([]);
  
  // State for filters and sort
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  
  // State for loading status
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load flavors on mount
  useEffect(() => {
    const loadFlavorsAndRatings = async () => {
      try {
        setLoading(true);
        
        // Add edition property to each flavor if it doesn't exist
        const flavorsWithEdition = (flavorData as any[]).map(flavor => ({
          ...flavor,
          edition: flavor.edition || 'Unknown Edition'
        })) as Flavor[];
        
        // Try to fetch ratings from GitHub
        try {
          const githubRatings = await getAllRatings();
          
          // Update flavors with GitHub ratings
          const flavorsWithRatings = flavorsWithEdition.map(flavor => {
            if (githubRatings[flavor.id]) {
              return {
                ...flavor,
                averageRating: githubRatings[flavor.id]
              };
            }
            return flavor;
          });
          
          setFlavors(flavorsWithRatings);
        } catch (ratingError) {
          console.error('Error fetching GitHub ratings:', ratingError);
          // If GitHub ratings fail, just use the original data
          setFlavors(flavorsWithEdition);
        }
        
        setLoading(false);
      } catch (err) {
        setError('Error loading flavors');
        setLoading(false);
        console.error('Error loading flavors:', err);
      }
    };
    
    loadFlavorsAndRatings();
  }, []);
  
  // Apply filters and sorting
  const filteredFlavors = useMemo(() => {
    if (!flavors.length) return [];
    
    // Apply filters
    let result = applyFilters(flavors, filters);
    
    // Apply sorting
    result = sortFlavors(result, sortBy);
    
    return result;
  }, [flavors, filters, sortBy]);
  
  // Update filters
  const updateFilters = (newFilters: Partial<Filters>) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({});
  };
  
  // Update sort
  const updateSort = (newSortBy: string) => {
    setSortBy(newSortBy);
  };
  
  return {
    flavors,
    filteredFlavors,
    filters,
    sortBy,
    loading,
    error,
    updateFilters,
    resetFilters,
    updateSort
  };
};
